import { AppContext } from "01Data/AppData";
import { PasswordPolicy } from "97Parts/Styles";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { CognitoUser } from "amazon-cognito-identity-js";
import { FC, useContext, useState } from "react";
import { Account } from "../01Data/Account";

type DialogResult = "ok" | "cancel" | "mfa";

type DialogProps = {
  open: boolean;
  onClose: (result: DialogResult) => void;
  account: Account;
  user: CognitoUser;
};

const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#f00",
}));

const AgreeEulaNote = styled("div")(({ theme }) => ({
  fontSize: "0.95rem",
  color: theme.palette.grey[800],
  margin: theme.spacing(2, 0, 2),
}));

const ForceChangePassDialogContent: FC<DialogProps> = ({
  open,
  onClose,
  account,
  user,
}) => {
  const [showPwd, setShowPwd] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeEula, setAgreeEula] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleApply = async () => {
    if (!newPassword && !confirmPassword) {
      setErrorMessage("パスワードを入力してください。");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("パスワードが一致しません。");
      return;
    }
    if (!newPassword.match(/^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?\d).{8,}$/)) {
      setErrorMessage("パスワードの形式が不正です。");
      return;
    }

    try {
      const output = await account.completeNewPassword(user, newPassword);
      // console.log(output.challengeName);
      if (output.challengeName === "MFA_SETUP") {
        onClose("mfa"); // MFA 強制時のみ
      } else {
        onClose("ok"); //
      }
      // console.log(newPassword);
    } catch (error: any) {
      setErrorMessage(
        `パスワードの更新に失敗しました。\n詳細: ${(error as Error).toString()}`
      );
    }
  };

  const handleAgreeEula = (e: any) => {
    setAgreeEula(e.target.checked);
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose("cancel")}
      aria-labelledby="force-change-pass-dialog-title"
      aria-describedby="force-change-pass-dialog-description"
    >
      <DialogTitle id="force-change-pass-dialog-title">
        パスワード変更
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="force-change-pass-dialog-description">
          パスワードの変更が必要です。
        </DialogContentText>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <TextField
            autoFocus
            required
            sx={{ width: "48%" }}
            type={showPwd ? "text" : "password"}
            variant="filled"
            margin="normal"
            id="newPassword"
            label="新しいパスワード"
            name="newPassword"
            onChange={(e) => {
              setNewPassword(e.target.value);
            }}
            inputProps={{ maxLength: 128 }}
            value={newPassword}
          />
          <TextField
            required
            sx={{ width: "48%" }}
            type={showPwd ? "text" : "password"}
            variant="filled"
            margin="normal"
            id="confirmPassword"
            label="確認用パスワード"
            name="confirmPassword"
            onChange={(e) => {
              setConfirmPassword(e.target.value);
            }}
            inputProps={{ maxLength: 128 }}
            value={confirmPassword}
          />
        </div>
        <PasswordPolicy>
          ※半角英大文字・半角英小文字・数字を含む、８文字以上のパスワードを設定してください。
        </PasswordPolicy>
        <FormControlLabel
          control={
            <Checkbox
              onChange={(e) => {
                setShowPwd(e.target.checked);
              }}
            />
          }
          label="パスワードを表示します"
        />
        <hr />
        <Typography variant="h6" color="text:secondary">
          利用規約
        </Typography>
        <AgreeEulaNote>
          ご利用にあたり、
          <a
            href={process.env.REACT_APP_URL_EULA}
            target="_blank"
            rel="noreferrer"
          >
            利用規約
          </a>
          への同意が必要です。
          <br />
          規約に同意いただける場合は、以下のチェックボックスにチェックを入れた上で次へお進みください。
        </AgreeEulaNote>
        <FormControlLabel
          control={<Checkbox onChange={handleAgreeEula} />}
          label={<span>利用規約に同意します</span>}
        />
        {errorMessage ? <ErrorText>{errorMessage}</ErrorText> : null}
      </DialogContent>
      <DialogActions>
        {agreeEula ? (
          <Button onClick={handleApply}>パスワード更新</Button>
        ) : (
          <Button disabled>パスワード更新</Button>
        )}
        <Button onClick={() => onClose("cancel")}>ログイン画面に戻る</Button>
      </DialogActions>
    </Dialog>
  );
};

export const useForceChangePassDialog = () => {
  const a = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [resolve, setResolve] = useState<(value: DialogResult) => void>();

  const openForceChangePassDialog = () => {
    setOpen(true);
    return new Promise<DialogResult>((resolve) => {
      setResolve(() => resolve);
    });
  };
  const onClose = (result: DialogResult) => {
    setOpen(false);
    if (resolve) {
      resolve(result);
    }
  };

  const ForceChangePassDialog: FC<{ user: CognitoUser }> = (props) => (
    <ForceChangePassDialogContent
      open={open}
      onClose={onClose}
      account={a.account}
      user={props.user}
    />
  );

  return {
    ForceChangePassDialog,
    openForceChangePassDialog,
  };
};
